/**
 * Salon Boss Lift widget entry.
 *
 * Finds every <div data-sb-widget data-merchant="..."> on the page, creates
 * a shadow root for each, and mounts the App. Style isolation via shadow DOM.
 *
 * Also supports legacy mount pattern: #sb-widget with data-merchant.
 *
 * Public `window.SalonLift` API is installed synchronously at module load
 * (before DOMContentLoaded) so consumer click handlers can call into it.
 * Phase 1 wires `on()` and `version`; open/mount/close land in Phase 3.
 */

import { render } from 'preact';
import { App } from './App';
import { type DeepLinkParams, parseDeepLinkParams } from './deep-link';
import { on as emitterOn } from './emitter';
import { createOverlay } from './overlay';
import { isPaymentInFlight } from './lib/payment-lock';
import { captureResumeToken, pendingResumeToken } from './lib/resume';
import styles from './styles.css?raw';

/**
 * Param shape accepted by `window.SalonLift.open(...)` and `.mount(...)`.
 * Phase 3 wires the implementations; this type is the contract.
 *
 * Fields mirror DeepLinkParams (service/staff/category/etc.) plus the
 * mount-time inputs that auto-mount picks up from data-attributes
 * (merchant, widgetSlug, apiBase).
 */
export interface OpenParams {
  merchant: string;
  widgetSlug?: string;
  service?: string;
  staff?: string;
  category?: string;
  headline?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  /** Pre-fill the catalog search bar (BeYou variant only currently). */
  q?: string;
  /** Phase 6 — open a Special Service's purchase directly, by product id. */
  product?: string;
  apiBase?: string;
  /**
   * Visual treatment for the `open()` overlay. `'modal'` (default) is a
   * centered card with scale-up; `'drawer'` slides in from the right edge.
   * Ignored by `mount(el)` since the caller owns the container.
   * Phase 5+ will let an operator default this per-widget in admin and
   * return it in the bootstrap response.
   */
  overlayStyle?: 'modal' | 'drawer';
}

/**
 * Convert imperative-API params to the same DeepLinkParams shape the
 * auto-mount path produces from URL query string. Keeps a single
 * downstream contract for the App's deep-link path.
 */
function paramsToDeepLink(params: OpenParams): DeepLinkParams {
  return {
    preselectedServiceId: params.service ?? null,
    preselectedStaffId: params.staff ?? null,
    prefilledContact: {
      firstName: params.firstName ?? null,
      lastName: params.lastName ?? null,
      email: params.email ?? null,
      phone: params.phone ?? null,
    },
    headline: params.headline ?? null,
    themeSlug: null,
    categoryId: params.category ?? null,
    searchQuery: params.q ?? null,
    // Phase 6 — salons whose site opens the widget itself (Be You opens
    // it on `?book=1`) never pass a product, so a `?product=` link on the
    // page would be lost. Only this one parameter falls back to the page
    // URL; every other one keeps coming from the call alone.
    productId: params.product ?? parseDeepLinkParams().productId ?? null,
  };
}

// Stamped at build time by Vite (see widget/vite.config.ts `define`).
// Falls back to a sentinel when this file is loaded outside the bundle
// (e.g. tsc --noEmit during typecheck) so the const stays defined.
declare const __SALONLIFT_VERSION__: string;
const SALONLIFT_VERSION =
  typeof __SALONLIFT_VERSION__ !== 'undefined' ? __SALONLIFT_VERSION__ : '0.0.0-untagged';

function uuidV4(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  if (c?.getRandomValues) {
    const bytes = new Uint8Array(16);
    c.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function resolveApiBase(scriptEl: HTMLScriptElement | null): string {
  // Allow explicit override via data attribute
  if (scriptEl?.dataset.apiBase) return scriptEl.dataset.apiBase;
  // Otherwise derive from script src origin
  if (scriptEl?.src) {
    try {
      return new URL(scriptEl.src).origin;
    } catch {
      // fall through
    }
  }
  // Last resort: current page origin (works for dev)
  return window.location.origin;
}

// Our own <script>, captured while this bundle executes — the only moment
// document.currentScript points at it. open()/mount() run long after load,
// and a later DOM search for `script[src*="widget.js"]` also matches other
// vendors' widget.js: Be You's /payment-options page loads Cherry's
// files.withcherry.com/widgets/widget.js, and once a visitor had been there
// (a client-side route change, so the tag stays) the LAST match was Cherry's.
// The API base became files.withcherry.com, the cached bootstrap hid it, and
// cart creation failed with "Failed to fetch" until a reload (2026-10-12).
const LOADING_SCRIPT: HTMLScriptElement | null =
  typeof document !== 'undefined' && document.currentScript instanceof HTMLScriptElement
    ? document.currentScript
    : null;

function getScriptElement(): HTMLScriptElement | null {
  if (LOADING_SCRIPT) return LOADING_SCRIPT;
  // Fallback (bundle evaluated without a currentScript): prefer a tag that
  // is unambiguously ours — our embed attributes, or a src that is exactly
  // /widget.js — over any other vendor's widget.js.
  const scripts = Array.from(document.querySelectorAll<HTMLScriptElement>('script[src*="widget.js"]'));
  const ours = scripts.filter((s) => {
    if (s.dataset.merchant || s.dataset.apiBase || s.dataset.salonLift !== undefined) return true;
    try {
      return new URL(s.src).pathname === '/widget.js';
    } catch {
      return false;
    }
  });
  return ours[ours.length - 1] ?? scripts[scripts.length - 1] ?? null;
}

// Loads the Google Fonts used by the design variants (Montserrat for the
// Be You bundle; Playfair Display reserved for the luxe display preset).
// CSS @font-face inside a shadow root works in some browsers but not all;
// a <link> on document.head is the reliable cross-browser path because
// the font binary lives at the document level and shadow-root CSS can
// reference it by family name. Idempotent — only inserts once even if
// the page hosts multiple widgets.
const GOOGLE_FONTS_LINK_ID = 'sb-widget-google-fonts';
function ensureGoogleFonts() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(GOOGLE_FONTS_LINK_ID)) return;
  const link = document.createElement('link');
  link.id = GOOGLE_FONTS_LINK_ID;
  link.rel = 'stylesheet';
  link.href =
    'https://fonts.googleapis.com/css2?' +
    'family=Montserrat:wght@300;400;500;600;700&' +
    'family=Playfair+Display:ital,wght@0,400;0,500;1,400;1,500&' +
    // PHD Studio variant — DM Sans body + Bodoni Moda display
    'family=DM+Sans:wght@400;500;600;700&' +
    'family=Bodoni+Moda:ital,wght@0,400;0,500;0,600;1,400&' +
    // Color Lush (sbw-tenant-color-lush-salon) — the site's own faces
    'family=Archivo+Black&' +
    'family=Inter:wght@400;500;600;700&' +
    'family=JetBrains+Mono:wght@400;500;600&' +
    'display=swap';
  document.head.appendChild(link);
}

/**
 * Mount the App into a host element. The caller (auto-mount via boot(),
 * or imperative open/mount in Phase 3) is responsible for producing the
 * DeepLinkParams. mountWidget itself does NOT read window.location — that
 * keeps the URL-driven path and the imperative path on the same downstream
 * contract.
 */
function mountWidget(
  host: HTMLElement,
  merchant: string,
  apiBase: string,
  widgetSlug: string | null,
  deepLink: DeepLinkParams,
) {
  if (host.shadowRoot) return; // already mounted
  const shadow = host.attachShadow({ mode: 'open' });
  // Load Google Fonts at the document level so they're available inside
  // every widget shadow root. Idempotent — multiple widgets share one
  // link tag. Includes Montserrat (Be You variant body + display) and
  // Playfair Display (luxe display fallback).
  ensureGoogleFonts();
  const styleEl = document.createElement('style');
  styleEl.textContent = styles;
  shadow.appendChild(styleEl);

  const mountPoint = document.createElement('div');
  // Style the mount point so .sbw-root's width:100%/height:100% resolves
  // against the host's actual size. Without this, the mountPoint sizes
  // itself to its (unbounded) shadow content and `.sbw-root { width:100% }`
  // becomes circular — the host width constraint never propagates in.
  mountPoint.style.cssText =
    'flex:1; display:flex; flex-direction:column; min-width:0; width:100%; height:100%;';
  shadow.appendChild(mountPoint);
  // Phase 7 Commit H — pass an explicit widget slug from the embed snippet
  // (or null for the tenant's default widget); the App forwards it to
  // bootstrap.
  render(
    <App
      merchant={merchant}
      apiBase={apiBase}
      deepLink={deepLink}
      widgetSlug={widgetSlug}
      sessionId={uuidV4()}
    />,
    mountPoint,
  );
  startGoodVibesShineLoop(mountPoint, merchant);
}

/**
 * JS-driven rotation for Good Vibes' Continue-button border shine.
 *
 * We can't use CSS @keyframes to animate the conic-gradient `from`
 * angle because animating a CSS custom property requires @property
 * registration, and @property does not register reliably inside the
 * widget's shadow DOM across browsers (we saw the gradient render
 * statically on prod even when the declaration was syntactically
 * present). Driving --sbw-gv-angle from rAF sidesteps that — every
 * frame we set the property on the mountPoint, the conic gradient
 * re-evaluates, the bright sweep advances around the perimeter.
 *
 * Scoped to the Good Vibes tenant only via slug check so no other
 * tenant pays the per-frame cost. Idempotent — calling twice for the
 * same mountPoint is a no-op (we mark the element with a data attr).
 */
function startGoodVibesShineLoop(mountPoint: HTMLElement, merchant: string): void {
  if (merchant !== 'good-vibes-salon') return;
  if (mountPoint.dataset.sbGvShine === '1') return;
  mountPoint.dataset.sbGvShine = '1';
  const SPEED_DEG_PER_MS = 360 / 3600; // 360° per 3.6s
  let angle = 0;
  let lastT = 0;
  function tick(t: number) {
    if (lastT) {
      angle = (angle + SPEED_DEG_PER_MS * (t - lastT)) % 360;
      mountPoint.style.setProperty('--sbw-gv-angle', `${angle}deg`);
    }
    lastT = t;
    requestAnimationFrame(tick);
  }
  requestAnimationFrame(tick);
}

function boot() {
  const scriptEl = getScriptElement();
  const apiBase = resolveApiBase(scriptEl);
  // D56 — parse deep-link params once per boot from the page URL. Each
  // auto-mounted host on the page shares the same URL-derived params;
  // imperative open()/mount() callers in Phase 3 will pass their own
  // params via paramsToDeepLink() and bypass this entirely.
  const deepLink = parseDeepLinkParams();

  // Legacy selector: #sb-widget. Opt out with data-auto-mount="false"
  // so consumer pages can pre-place a host element AND still drive the
  // widget exclusively via window.SalonLift.open(...).
  let mounted = false;
  const legacy = document.getElementById('sb-widget');
  if (legacy && legacy.dataset.merchant && legacy.dataset.autoMount !== 'false') {
    mounted = true;
    mountWidget(
      legacy,
      legacy.dataset.merchant,
      apiBase,
      legacy.dataset.widgetSlug ?? null,
      deepLink,
    );
  }

  // Modern selector: [data-sb-widget]. Same auto-mount opt-out applies.
  document.querySelectorAll<HTMLElement>('[data-sb-widget]').forEach((host) => {
    if (host.dataset.autoMount === 'false') return;
    const merchant = host.dataset.merchant;
    if (!merchant) {
      console.warn('[Salon Boss Lift widget] data-merchant attribute required');
      return;
    }
    // Phase 7 Commit H — `data-widget-slug` picks a non-default widget;
    // omit it to mount the tenant's default. Operators copy the right
    // attribute from the per-widget admin Embed Snippet card.
    mounted = true;
    mountWidget(host, merchant, apiBase, host.dataset.widgetSlug ?? null, deepLink);
  });

  // P5.2 D — back from a payment redirect with nothing on the page to
  // show the widget: open it, so the customer sees their booking finish
  // instead of a page with no widget. An auto-mounted widget above picks
  // the resume up by itself. Pages that drive the widget themselves open
  // it on `book=1` (the return URL carries it — Be You's integration
  // does), so this waits a moment and stands down if anything has opened
  // one: two instances would race the same single-use token. Needs the
  // merchant on the script tag; without it the page's own code is the
  // only way back.
  if (pendingResumeToken() && !mounted) {
    const merchant = scriptEl?.dataset.merchant;
    if (merchant) {
      window.setTimeout(() => {
        if (currentSession) return;
        openImpl({ merchant, widgetSlug: scriptEl?.dataset.widgetSlug });
      }, RESUME_AUTO_OPEN_DELAY_MS);
    }
  }
}

/** How long the auto-open waits for the page's own code to open the widget. */
const RESUME_AUTO_OPEN_DELAY_MS = 1500;

// ---------------------------------------------------------------------------
// Imperative API lifecycle.
//
// `currentSession` is the single active imperative mount. open()/mount()
// close-and-replace any prior session. Auto-mounted [data-sb-widget] hosts
// are independent — they don't share state with this ref.
// ---------------------------------------------------------------------------

interface CurrentSession {
  mountPoint: HTMLElement;
  sessionId: string;
  // Mount-type-specific teardown. For open(): overlay.destroy() — removes
  // backdrop + host, drops listeners, restores body scroll + prior focus.
  // For mount(el): a no-op — the caller owns the element; close() just
  // unmounts Preact and leaves the element in the DOM.
  cleanup: () => void;
}

let currentSession: CurrentSession | null = null;

interface SessionHandle {
  id: string;
  close(): void;
}

const MOUNT_POINT_STYLES =
  'flex:1; display:flex; flex-direction:column; min-width:0; width:100%; height:100%;';

/** Set up (or reuse) a shadow root + mountPoint on a caller-supplied element. */
function setupShadowMount(el: HTMLElement): HTMLElement {
  // Load Google Fonts at document level — needed for any imperative
  // mount path (open/mount) the same way mountWidget() does for auto-
  // mount. Idempotent, so calling twice when both paths run is harmless.
  ensureGoogleFonts();
  if (el.shadowRoot) {
    const existing = el.shadowRoot.querySelector<HTMLDivElement>(':scope > div[data-sb-mount]');
    if (!existing) {
      throw new Error(
        '[SalonLift] mount(el): el has a non-SalonLift shadow root. Use a fresh element.',
      );
    }
    return existing;
  }
  const shadow = el.attachShadow({ mode: 'open' });
  const styleEl = document.createElement('style');
  styleEl.textContent = styles;
  shadow.appendChild(styleEl);
  const mountPoint = document.createElement('div');
  mountPoint.setAttribute('data-sb-mount', '');
  mountPoint.style.cssText = MOUNT_POINT_STYLES;
  shadow.appendChild(mountPoint);
  return mountPoint;
}

function openImpl(params: OpenParams): SessionHandle {
  if (!params || !params.merchant) {
    throw new Error('[SalonLift] open(): params.merchant is required');
  }
  // Close-and-replace any prior session BEFORE creating the new overlay.
  // Order matters: createOverlay snapshots the focused element + body
  // overflow; running closeImpl() afterwards would restore the OLD
  // session's prior focus on top of our new snapshot.
  closeImpl();

  const overlay = createOverlay({
    onClose: closeImpl,
    appStyles: styles,
    style: params.overlayStyle ?? 'modal',
  });
  const sessionId = uuidV4();
  try {
    const deepLink = paramsToDeepLink(params);
    const apiBase = params.apiBase ?? resolveApiBase(getScriptElement());
    render(
      <App
        merchant={params.merchant}
        apiBase={apiBase}
        deepLink={deepLink}
        widgetSlug={params.widgetSlug ?? null}
        sessionId={sessionId}
      />,
      overlay.mountPoint,
    );
    startGoodVibesShineLoop(overlay.mountPoint, params.merchant);
  } catch (e) {
    overlay.destroy();
    throw e;
  }
  currentSession = {
    mountPoint: overlay.mountPoint,
    sessionId,
    cleanup: overlay.destroy,
  };
  return { id: sessionId, close: closeImpl };
}

function mountImpl(el: HTMLElement, params: OpenParams): SessionHandle {
  if (!el || !(el instanceof HTMLElement)) {
    throw new Error('[SalonLift] mount(): first argument must be an HTMLElement');
  }
  if (!params || !params.merchant) {
    throw new Error('[SalonLift] mount(): params.merchant is required');
  }
  closeImpl();
  const mountPoint = setupShadowMount(el);
  const sessionId = uuidV4();
  const deepLink = paramsToDeepLink(params);
  const apiBase = params.apiBase ?? resolveApiBase(getScriptElement());
  render(
    <App
      merchant={params.merchant}
      apiBase={apiBase}
      deepLink={deepLink}
      widgetSlug={params.widgetSlug ?? null}
      sessionId={sessionId}
    />,
    mountPoint,
  );
  startGoodVibesShineLoop(mountPoint, params.merchant);
  currentSession = {
    mountPoint,
    sessionId,
    // mount(el) doesn't own the element — leave it in the DOM. Shadow
    // root persists for re-mount on the same element.
    cleanup: () => {
      /* no-op */
    },
  };
  return { id: sessionId, close: closeImpl };
}

function closeImpl(): void {
  // A payment is mid-confirm. Closing runs render(null, ...), which runs
  // the Payment step's cleanup, which destroys the Stripe Element the
  // confirm call is running against — the customer would watch the
  // widget disappear with a charge possibly in flight. All four ways to
  // close (backdrop, Escape, close button, the salon's own
  // SalonLift.close()) arrive here, so this one guard covers them all.
  //
  // Inert unless something raised the lock, and only the Payment Element
  // path does. See lib/payment-lock.
  if (!currentSession) return;
  // Only THIS session's payment counts: an inline widget paying on the
  // same page is not a reason to refuse closing the overlay.
  if (isPaymentInFlight(currentSession.sessionId)) {
    // eslint-disable-next-line no-console
    console.warn('[SalonLift] close ignored — a payment is being confirmed');
    return;
  }
  const { mountPoint, cleanup } = currentSession;
  // Preact unmount first — runs every step's useEffect cleanup (incl.
  // Payment portal teardown). Then run the mount-type-specific cleanup
  // (overlay destroy or no-op).
  render(null, mountPoint);
  currentSession = null;
  try {
    cleanup();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[SalonLift] cleanup threw', e);
  }
}

// Install the public global synchronously, before DOMContentLoaded, so
// consumer pages can attach listeners and call open()/mount()/close()
// from inline scripts that run before the auto-mount path fires.
(window as unknown as { SalonLift: unknown }).SalonLift = {
  version: SALONLIFT_VERSION,
  on: emitterOn,
  open: openImpl,
  mount: mountImpl,
  close: closeImpl,
};

// eslint-disable-next-line no-console
console.info('[SalonLift] widget.js loaded', SALONLIFT_VERSION);

// P5.2 D — take a payment redirect's resume token off the URL now, before
// anything else on the page logs it again. Every widget instance reads it
// from here; see lib/resume.
captureResumeToken();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
